import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiClock, FiUsers, FiCheck } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./CuisineDetail.css";

import plovImg from "../photos/food4.webp";
import mantiImg from "../photos/manti.jpg";
import samsaImg from "../photos/somsa.jpg";
import lagmanImg from "../photos/lagmon.jpg";

const dishes = {
    plov: {
        image: plovImg,
    },
    manti: {
        image: mantiImg,
    },
    samsa: {
        image: samsaImg,
    },
    lagman: {
        image: lagmanImg,
    },
};

export default function CuisineDetail() {
    const { id } = useParams();
    const { t, i18n } = useTranslation();

    const dish = dishes[id];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    /*
     * ========================================
     * GET CURRENT LANGUAGE DATA
     * ========================================
     */

    const languageData = i18n.getResourceBundle(
        i18n.language,
        "translation"
    );

    const localizedDish =
        languageData?.cuisine_detail?.dishes?.[id];

    /*
     * ========================================
     * INGREDIENTS & STEPS
     * ========================================
     */

    const ingredients = Array.isArray(
        localizedDish?.ingredients
    )
        ? localizedDish.ingredients
        : [];

    const steps = Array.isArray(
        localizedDish?.steps
    )
        ? localizedDish.steps
        : [];

    /*
     * ========================================
     * NOT FOUND
     * ========================================
     */

    if (!dish || !localizedDish) {
        return (
            <main className="cuisine-detail-page">
                <div className="cuisine-not-found">
                    <span className="detail-label">
                        ADAM VOYAGE
                    </span>

                    <h1>
                        {t(
                            "cuisine_detail.not_found_title"
                        )}
                    </h1>

                    <p>
                        {t(
                            "cuisine_detail.not_found_description"
                        )}
                    </p>

                    <Link to="/cuisine">
                        <FiArrowLeft />

                        {t(
                            "cuisine_detail.back_to_dishes"
                        )}
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main className="cuisine-detail-page">
            <Helmet>
                <title>
                    {localizedDish.title} | Adam Voyage
                </title>

                <meta
                    name="description"
                    content={
                        localizedDish.seo_description
                    }
                />
            </Helmet>

            {/* ========================================
                HERO
            ======================================== */}

            <section className="detail-hero">
                <div
                    className="detail-hero-image"
                    style={{
                        backgroundImage: `url(${dish.image})`,
                    }}
                />

                <div className="detail-hero-overlay" />

                <div className="detail-hero-content">
                    <Link
                        to="/cuisine"
                        className="detail-back"
                    >
                        <FiArrowLeft />

                        {t(
                            "cuisine_detail.all_dishes"
                        )}
                    </Link>

                    <span className="detail-eyebrow">
                        {t(
                            "cuisine_detail.hero_eyebrow"
                        )}
                    </span>

                    <h1>
                        {localizedDish.title}
                    </h1>

                    <p>
                        {localizedDish.subtitle}
                    </p>
                </div>
            </section>

            {/* ========================================
                CONTENT
            ======================================== */}

            <section className="detail-content">
                <div className="detail-main">

                    {/* ABOUT */}

                    <div className="detail-intro">
                        <span className="detail-label">
                            {t(
                                "cuisine_detail.about_label"
                            )}
                        </span>

                        <h2>
                            {t(
                                "cuisine_detail.about_title"
                            )}
                        </h2>

                        <p>
                            {localizedDish.intro}
                        </p>
                    </div>

                    {/* INGREDIENTS */}

                    <div className="detail-ingredients">
                        <div className="detail-heading">
                            <span className="detail-label">
                                {t(
                                    "cuisine_detail.ingredients_label"
                                )}
                            </span>

                            <h2>
                                {t(
                                    "cuisine_detail.ingredients_title"
                                )}
                            </h2>
                        </div>

                        <div className="ingredients-grid">
                            {ingredients.map(
                                (ingredient, index) => (
                                    <div
                                        className="ingredient"
                                        key={`${index}-${ingredient}`}
                                    >
                                        <FiCheck />

                                        <span>
                                            {ingredient}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                    </div>

                    {/* RECIPE */}

                    <div className="detail-steps">
                        <div className="detail-heading">
                            <span className="detail-label">
                                {t(
                                    "cuisine_detail.recipe_label"
                                )}
                            </span>

                            <h2>
                                {t(
                                    "cuisine_detail.recipe_title"
                                )}
                            </h2>
                        </div>

                        <div className="steps-list">
                            {steps.map(
                                (step, index) => (
                                    <div
                                        className="recipe-step"
                                        key={index}
                                    >
                                        <div className="step-number">
                                            {String(
                                                index + 1
                                            ).padStart(
                                                2,
                                                "0"
                                            )}
                                        </div>

                                        <div className="step-content">
                                            <h3>
                                                {
                                                    step.title
                                                }
                                            </h3>

                                            <p>
                                                {
                                                    step.text
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>

                {/* ========================================
                    SIDEBAR
                ======================================== */}

                <aside className="detail-sidebar">
                    <div className="detail-info-card">
                        <span className="detail-label">
                            {t(
                                "cuisine_detail.info_label"
                            )}
                        </span>

                        <div className="info-item">
                            <div className="info-icon">
                                <FiClock />
                            </div>

                            <div>
                                <span>
                                    {t(
                                        "cuisine_detail.preparation_time"
                                    )}
                                </span>

                                <strong>
                                    {localizedDish.time}
                                </strong>
                            </div>
                        </div>

                        <div className="info-item">
                            <div className="info-icon">
                                <FiUsers />
                            </div>

                            <div>
                                <span>
                                    {t(
                                        "cuisine_detail.servings_label"
                                    )}
                                </span>

                                <strong>
                                    {localizedDish.servings}
                                </strong>
                            </div>
                        </div>
                    </div>

                    {/* TIP */}

                    <div className="detail-tip-card">
                        <span>
                            ADAM VOYAGE
                        </span>

                        <h3>
                            {t(
                                "cuisine_detail.tip_title"
                            )}
                        </h3>

                        <p>
                            {t(
                                "cuisine_detail.tip_description"
                            )}
                        </p>
                    </div>
                </aside>
            </section>

            {/* ========================================
                FOOTER
            ======================================== */}

            <section className="detail-footer">
                <span>
                    ADAM VOYAGE
                </span>

                <h2>
                    {t(
                        "cuisine_detail.footer_title"
                    )}

                    <br />

                    {t(
                        "cuisine_detail.footer_title_highlight"
                    )}
                </h2>

                <Link to="/cuisine">
                    <FiArrowLeft />

                    {t(
                        "cuisine_detail.back_to_list"
                    )}
                </Link>
            </section>
        </main>
    );
}